import React, { useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { advanceOnboardingStateAction } from 'Shared/actions/onboardingState';

import Terminal from '../Terminal/Terminal';
import { OnboardingWizardStep, OnboardingState } from '../types';
import PageWrapper from './Wrappers/PageWrapper/PageWrapper';
import Profile from './Profile/Profile';
import ScheduleDemo from './ScheduleDemo/ScheduleDemo';
import ConnectCalendar from './ConnectCalendar/ConnectCalendar';
import Workspaces from './Workspaces/Workspaces';
import OneOnOneWorkspacesFromCalendar from './OneOnOneWorkspacesFromCalendar/OneOnOneWorkspacesFromCalendar';
import GroupWorkspacesFromCalendar from './GroupWorkspacesFromCalendar/GroupWorkspacesFromCalendar';
import DiscussionTopics from './DiscussionTopics/DiscussionTopics';
import CreateAccount from './CreateAccount/CreateAccount';
import InviteTeammates from './InviteTeammates/InviteTeammates';

function MobileOnboardingWizard() {
  const {
    profile,
    workspaces,
    discussionTopics,
    step,
    numDirectReports,
    flowType,
    forceToDesktop,
    cannotSignIntoGoogle,
    recurringMeetings,
    userCreatedInMiddle,
  }: OnboardingState = useSelector(state => state.onboardingState);
  const dispatch = useDispatch();

  const oneOnOneRecurringMeetings = recurringMeetings.filter(({ isGroup }) => !isGroup);
  const groupRecurringMeetings = recurringMeetings.filter(({ isGroup }) => isGroup);

  if (step) {
    (window as any).analytics.identify({ flowType });
    (window as any).analytics.track('Started Onboarding Step', { step }, { integrations: { Calixa: false } });
  }

  const handleAdvanceOnboardingState = useCallback(
    update => dispatch(advanceOnboardingStateAction({ onboardingState: update, didSkip: false })),
    [dispatch]
  );

  const currentContent = () => {
    switch (step) {
      case OnboardingWizardStep.scheduleDemo:
        return <ScheduleDemo advanceOnboardingState={handleAdvanceOnboardingState} />;
      case OnboardingWizardStep.profile:
        return <Profile advanceOnboardingState={handleAdvanceOnboardingState} profile={profile} />;
      case OnboardingWizardStep.connectCalendar:
        return (
          <ConnectCalendar userCreatedInMiddle={userCreatedInMiddle} cannotSignIntoGoogle={cannotSignIntoGoogle} />
        );
      case OnboardingWizardStep.workspaces:
        return (
          <Workspaces
            advanceOnboardingState={handleAdvanceOnboardingState}
            workspaces={workspaces}
            numDirectReports={numDirectReports}
          />
        );
      case OnboardingWizardStep.oneOnOneWorkspacesFromCalendar:
        return (
          <OneOnOneWorkspacesFromCalendar
            advanceOnboardingState={handleAdvanceOnboardingState}
            workspaces={workspaces}
            recurringMeetings={oneOnOneRecurringMeetings}
          />
        );
      case OnboardingWizardStep.groupWorkspacesFromCalendar:
        return (
          <GroupWorkspacesFromCalendar
            advanceOnboardingState={handleAdvanceOnboardingState}
            workspaces={workspaces}
            recurringMeetings={groupRecurringMeetings}
          />
        );
      case OnboardingWizardStep.inviteTeammates:
        return (
          <InviteTeammates
            advanceOnboardingState={handleAdvanceOnboardingState}
            workspaces={workspaces}
            recurringMeetings={recurringMeetings}
          />
        );
      case OnboardingWizardStep.discussionTopics:
        return (
          <DiscussionTopics advanceOnboardingState={handleAdvanceOnboardingState} discussionTopics={discussionTopics} />
        );
      default:
        return (
          <CreateAccount
            profile={profile}
            cannotSignIntoGoogle={cannotSignIntoGoogle}
            forceToDesktop={forceToDesktop}
          />
        );
    }
  };

  if (step === OnboardingWizardStep.terminal) return <Terminal />;

  return <PageWrapper>{currentContent()}</PageWrapper>;
}

export default memo(MobileOnboardingWizard);
